/**
 * TOP-N Bericht — flexibel: Dimension, Measure, Limit, mit Tabelle + Chart + Export.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { ExportMenu } from '../components/ExportMenu.tsx';
import { TableSkeleton, ChartSkeleton } from '../components/Skeleton.tsx';
import { fmtCurrency, fmtCurrencyCompact, fmtNumber, fmtPercent } from '../lib/format.ts';
import { axisStyle, tooltipStyle, COLORS } from '../lib/echarts-theme.ts';
import type { TopNRequest } from '@statistik/shared-types';

type Dim = TopNRequest['dimension'];
type Measure = TopNRequest['measure'];

const DIM_LABELS: Record<Dim, string> = {
  store_name: 'Filiale',
  store_number: 'Filial-Nr',
  warengruppe_name: 'Warengruppe',
  artikel_bezeichnung: 'Artikel',
  artikel_nr: 'Artikel-Nr (Monolith)',
  ean: 'EAN',
  kassierer_name: 'Kassierer',
  plz: 'PLZ',
};

const MEASURE_LABELS: Record<Measure, string> = {
  umsatz: 'Umsatz',
  menge: 'Menge',
  anz_bons: 'Anzahl Bons',
};

export function TopNReportPage() {
  const [params, setParams] = useSearchParams();
  const filters = useFilterStore((s) => s.filters);
  const dim = (params.get('dim') as Dim) || 'artikel_bezeichnung';
  const measure = (params.get('m') as Measure) || 'umsatz';
  const [limit, setLimit] = useState<number>(Number(params.get('n')) || 100);

  const { data, isLoading } = useQuery({
    queryKey: ['topn-report', dim, measure, limit, filters],
    queryFn: () => api.topn({ filters, dimension: dim, measure, limit }),
  });

  function updateDim(d: Dim) {
    params.set('dim', d);
    setParams(params, { replace: true });
  }
  function updateMeasure(m: Measure) {
    params.set('m', m);
    setParams(params, { replace: true });
  }
  function updateLimit(n: number) {
    setLimit(n);
    params.set('n', String(n));
    setParams(params, { replace: true });
  }

  // Label-Anreicherung: WG-Nr als Präfix, Artikel-Nr als Suffix (analog zu
  // TopNChart / Sidebar / FilterChips — überall einheitlich).
  const isWg = dim === 'warengruppe_name';
  const enrichLabel = (label: string, sub_label?: string): string => {
    if (!sub_label) return label;
    return isWg ? `${sub_label} — ${label}` : `${label}  ·  #${sub_label}`;
  };

  const chartOption = (() => {
    if (!data || data.length === 0) return {};
    // Chart zeigt nur die ersten 20 Einträge (sonst gequetscht), Tabelle alle.
    const top20 = data.slice(0, 20);
    const reversed = [...top20].reverse();
    return {
      grid: { top: 8, right: 80, bottom: 28, left: 4, containLabel: true },
      tooltip: {
        ...tooltipStyle(),
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      xAxis: {
        type: 'value',
        ...axisStyle(),
        axisLabel: {
          ...axisStyle().axisLabel,
          formatter: (v: number) =>
            measure === 'umsatz'
              ? fmtCurrencyCompact(v)
              : v >= 1000
                ? `${(v / 1000).toFixed(1)}k`
                : String(v),
        },
      },
      yAxis: {
        type: 'category',
        data: reversed.map((d) => enrichLabel(d.label, d.sub_label)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: COLORS.text, fontSize: 11, width: 200, overflow: 'truncate' },
      },
      series: [
        {
          type: 'bar',
          data: reversed.map((d) => d.wert),
          barCategoryGap: '30%',
          itemStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: '#0e7bd1' },
                { offset: 1, color: '#4cb1ff' },
              ],
            },
            borderRadius: [0, 4, 4, 0],
          },
          label: {
            show: true,
            position: 'right',
            formatter: (p: { value: number }) =>
              measure === 'umsatz' ? fmtCurrencyCompact(p.value) : fmtNumber(p.value),
            fontSize: 11,
            color: COLORS.text,
            fontWeight: 600,
          },
        },
      ],
    };
  })();

  return (
    <div className="space-y-4 lg:space-y-6 w-full">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          TOP {limit} Bericht
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Flexibles Ranking nach beliebiger Dimension und Kennzahl. Filter aus der
          Sidebar werden übernommen.
        </p>
      </div>

      {/* Filter-Bar */}
      <div className="card card-body">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 mb-1">
              Dimension
            </label>
            <select
              value={dim}
              onChange={(e) => updateDim(e.target.value as Dim)}
              className="input"
            >
              {Object.entries(DIM_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 mb-1">
              Kennzahl
            </label>
            <select
              value={measure}
              onChange={(e) => updateMeasure(e.target.value as Measure)}
              className="input"
            >
              {Object.entries(MEASURE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-2xs uppercase tracking-wider font-medium text-slate-500 mb-1">
              Anzahl
            </label>
            <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
              {[10, 25, 50, 100].map((n) => (
                <button
                  key={n}
                  onClick={() => updateLimit(n)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                    limit === n
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="ml-auto">
            <ExportMenu
              rows={(data ?? []).map((r, i) => ({
                rang: i + 1,
                label: enrichLabel(r.label, r.sub_label),
                wert: r.wert,
                anteil: r.anteil,
              }))}
              columns={[
                { header: '#', key: 'rang', numeric: true },
                { header: DIM_LABELS[dim], key: 'label', width: 40 },
                {
                  header: MEASURE_LABELS[measure],
                  key: 'wert',
                  numeric: true,
                  format: (v) =>
                    measure === 'umsatz' ? fmtCurrency(Number(v)) : fmtNumber(Number(v)),
                },
                {
                  header: 'Anteil',
                  key: 'anteil',
                  format: (v) => fmtPercent(Number(v)),
                },
              ]}
              filename={`TOP${limit}-${dim}-${measure}`}
              title={`TOP ${limit} ${DIM_LABELS[dim]} nach ${MEASURE_LABELS[measure]}`}
            />
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title">
              TOP {Math.min(20, limit)} — Visualisierung
            </h3>
            <p className="card-subtitle">
              Größere Werte oben. Klick auf einen Balken setzt den Filter.
            </p>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <ChartSkeleton height={Math.min(20, limit) * 30 + 40} />
          ) : (
            <ReactECharts
              option={chartOption}
              style={{ height: Math.min(20, limit) * 30 + 40 }}
              opts={{ renderer: 'canvas' }}
              notMerge={true}
            />
          )}
        </div>
      </div>

      {/* Tabelle */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Detail-Tabelle</h3>
          <p className="card-subtitle text-right">
            {data?.length ?? 0} Einträge
          </p>
        </div>
        <div className="card-body">
          {isLoading ? (
            <TableSkeleton rows={10} />
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-12">#</th>
                    <th>{DIM_LABELS[dim]}</th>
                    <th className="num">{MEASURE_LABELS[measure]}</th>
                    <th className="num">Anteil</th>
                    <th className="w-24">
                      <span className="sr-only">Balken</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((r, i) => (
                    <tr key={r.label + i}>
                      <td className="font-mono text-2xs text-slate-400">{i + 1}</td>
                      <td className="font-medium">
                        {r.sub_label && isWg ? (
                          <>
                            <span className="inline-flex items-center justify-center min-w-[2rem] h-5 px-1.5 rounded text-2xs font-semibold bg-brand-100 dark:bg-brand-900/60 text-brand-700 dark:text-brand-300 mr-2 tabular-nums">
                              {r.sub_label}
                            </span>
                            {r.label}
                          </>
                        ) : r.sub_label ? (
                          <>
                            {r.label}
                            <span className="ml-2 font-mono text-2xs text-slate-400">
                              #{r.sub_label}
                            </span>
                          </>
                        ) : (
                          r.label
                        )}
                      </td>
                      <td className="num font-semibold tabular-nums">
                        {measure === 'umsatz'
                          ? fmtCurrency(r.wert)
                          : fmtNumber(r.wert)}
                      </td>
                      <td className="num tabular-nums text-slate-500">
                        {fmtPercent(r.anteil)}
                      </td>
                      <td>
                        <div className="w-full h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-brand-600 to-brand-400"
                            style={{ width: `${Math.min(100, r.anteil * 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
